import { Button, useTranslation } from '@hbs-us/ui';
import { useEffect, useState } from 'react';

import { useAccess } from '../../../auth/useAccess.js';
import { useCompanySettingsMutation, useSettingsQuery } from '../useSettingsQuery.js';

/**
 * Who may make a phone sign-in code (`managersIssueLoginCodes`, default on: owners and managers).
 * Owners always may. Only an owner changes it; anyone else who can open Settings reads it. The
 * card hides until the server sends the key, because the server refuses a key it does not know.
 *
 * It saves on its own rather than through the page's Save bar: company rules are owner-only on
 * the server, and the page's Save is open to anyone with settings.manage.
 */
export function SignInCodeSettingsCard() {
  const { t } = useTranslation();
  const canEdit = useAccess()({ minRole: 'Admin' });
  const stored = useSettingsQuery().data?.companySettings?.managersIssueLoginCodes;
  const save = useCompanySettingsMutation();
  const [managers, setManagers] = useState(stored === true);

  useEffect(() => {
    if (typeof stored === 'boolean') setManagers(stored);
  }, [stored]);

  if (typeof stored !== 'boolean') return null;

  return (
    <section className="rounded-md border border-border bg-card p-6">
      <h2 className="mb-1 font-display font-semibold text-foreground text-lg">
        {t('settings:signInCodes.heading')}
      </h2>
      <p className="mb-4 text-muted-foreground text-sm">
        {canEdit ? t('settings:signInCodes.description') : t('settings:signInCodes.ownersOnly')}
      </p>
      <label className="flex min-h-11 items-start gap-3">
        <input
          type="checkbox"
          className="mt-0.5 size-5 shrink-0 accent-primary disabled:cursor-not-allowed"
          checked={managers}
          disabled={!canEdit}
          onChange={(e) => setManagers(e.target.checked)}
        />
        <span>
          <span className="font-medium text-foreground text-sm">
            {t('settings:signInCodes.managers')}
          </span>
          <span className="mt-1 block text-muted-foreground text-xs">
            {t('settings:signInCodes.managersHint')}
          </span>
        </span>
      </label>
      {canEdit ? (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
          {save.isError ? (
            <p role="alert" className="mr-auto text-destructive text-sm">
              {t('settings:signInCodes.saveError')}
            </p>
          ) : null}
          {save.isSuccess ? (
            <p role="status" className="mr-auto text-muted-foreground text-sm">
              {t('settings:signInCodes.saved')}
            </p>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            disabled={save.isPending || managers === stored}
            onClick={() => save.mutate({ managersIssueLoginCodes: managers })}
          >
            {t('settings:signInCodes.save')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
